import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CreditCardIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  PrinterIcon,
  CalendarIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const PaymentHistory = ({ payments = [] }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');

  // Delete payment mutation
  const deletePaymentMutation = useMutation(
    (paymentId) => axios.delete(`/payments/${paymentId}`),
    {
      onSuccess: () => {
        toast.success('Paiement supprimé avec succès!');
        queryClient.invalidateQueries('payments');
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      },
    }
  );

  // Update payment status mutation
  const updateStatusMutation = useMutation(
    ({ id, status }) => axios.patch(`/payments/${id}/status`, { status }),
    {
      onSuccess: () => {
        toast.success('Statut mis à jour avec succès!');
        queryClient.invalidateQueries('payments');
      },
      onError: () => {
        toast.error('Erreur lors de la mise à jour du statut');
      },
    }
  );

  const paymentMethods = [
    { id: 'cash', name: 'Espèces', icon: BanknotesIcon, color: 'bg-green-500' },
    { id: 'card', name: 'Carte', icon: CreditCardIcon, color: 'bg-blue-500' },
    { id: 'transfer', name: 'Virement', icon: ArrowTrendingUpIcon, color: 'bg-purple-500' },
    { id: 'check', name: 'Chèque', icon: DocumentTextIcon, color: 'bg-orange-500' },
  ];

  // Filter and sort payments
  const filteredPayments = payments
    .filter(payment => {
      const matchesSearch = payment.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           payment.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           payment.reference?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
      const matchesMethod = methodFilter === 'all' || payment.paymentMethod === methodFilter;
      
      let matchesDate = true;
      if (dateRange !== 'all') {
        const paymentDate = new Date(payment.paymentDate);
        const now = new Date();
        
        switch (dateRange) {
          case 'today':
            matchesDate = paymentDate.toDateString() === now.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = paymentDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = paymentDate >= monthAgo;
            break;
          default:
            matchesDate = true;
        }
      }
      
      return matchesSearch && matchesStatus && matchesMethod && matchesDate;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.paymentDate) - new Date(a.paymentDate);
        case 'date-asc':
          return new Date(a.paymentDate) - new Date(b.paymentDate);
        case 'amount-desc':
          return (b.amount || 0) - (a.amount || 0);
        case 'amount-asc':
          return (a.amount || 0) - (b.amount || 0);
        case 'member':
          return (a.memberName || '').localeCompare(b.memberName || '');
        default:
          return 0;
      }
    });

  const handleDeletePayment = (paymentId, memberName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le paiement de ${memberName} ?`)) {
      deletePaymentMutation.mutate(paymentId);
    }
  };

  const handleUpdateStatus = (paymentId, newStatus) => {
    updateStatusMutation.mutate({ id: paymentId, status: newStatus });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'overdue':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'cancelled':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      paid: 'Payé',
      pending: 'En attente',
      overdue: 'En retard',
      cancelled: 'Annulé',
    };
    return statusTexts[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return CheckCircleIcon;
      case 'pending':
        return ClockIcon;
      case 'overdue':
        return XCircleIcon;
      case 'cancelled':
        return XCircleIcon;
      default:
        return ClockIcon;
    }
  };

  // Calculate totals
  const totals = filteredPayments.reduce((acc, payment) => {
    acc.total += payment.amount || 0;
    acc.count += 1;
    if (payment.status === 'paid') {
      acc.paid += payment.amount || 0;
      acc.paidCount += 1;
    }
    return acc;
  }, { total: 0, paid: 0, count: 0, paidCount: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total Paiements</p>
              <p className="text-xl font-bold text-white">{totals.count}</p>
            </div>
            <div className="p-2 bg-blue-500/20 rounded-xl">
              <CreditCardIcon className="h-5 w-5 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Payés</p>
              <p className="text-xl font-bold text-white">{totals.paidCount}</p>
            </div>
            <div className="p-2 bg-green-500/20 rounded-xl">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Montant Total</p>
              <p className="text-xl font-bold text-white">{totals.total.toLocaleString()} DA</p>
            </div>
            <div className="p-2 bg-purple-500/20 rounded-xl">
              <BanknotesIcon className="h-5 w-5 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Encaissé</p>
              <p className="text-xl font-bold text-white">{totals.paid.toLocaleString()} DA</p>
            </div>
            <div className="p-2 bg-green-500/20 rounded-xl">
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="paid">Payés</option>
            <option value="pending">En attente</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulés</option>
          </select>

          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les modes</option>
            <option value="cash">Espèces</option>
            <option value="card">Carte</option>
            <option value="transfer">Virement</option>
            <option value="check">Chèque</option>
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Toutes les dates</option>
            <option value="today">Aujourd'hui</option>
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="date-desc">Date (récent)</option>
            <option value="date-asc">Date (ancien)</option>
            <option value="amount-desc">Montant (élevé)</option>
            <option value="amount-asc">Montant (faible)</option>
            <option value="member">Nom du membre</option>
          </select>
        </div>
      </div>

      {/* Payment List */}
      <div className="space-y-4">
        {filteredPayments.length > 0 ? (
          filteredPayments.map((payment, index) => {
            const method = paymentMethods.find(m => m.id === payment.paymentMethod);
            const StatusIcon = getStatusIcon(payment.status);
            
            return (
              <motion.div
                key={payment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${method?.color || 'bg-gray-500'}`}>
                      {method?.icon && React.createElement(method.icon, { className: "h-6 w-6 text-white" })}
                    </div>
                    
                    <div>
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-white">{payment.memberName}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(payment.status)}`}>
                          {getStatusText(payment.status)}
                        </span>
                      </div>
                      <p className="text-white/60 text-sm mt-1">{payment.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-white/50 text-sm">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{new Date(payment.paymentDate).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <UserIcon className="h-4 w-4" />
                          <span>{payment.memberCode}</span>
                        </div>
                        {payment.reference && (
                          <div className="flex items-center space-x-1">
                            <DocumentTextIcon className="h-4 w-4" />
                            <span>{payment.reference}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">{payment.amount?.toLocaleString()} DA</p>
                      <p className="text-white/60 text-sm">{method?.name}</p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {payment.status === 'pending' && (
                        <button
                          onClick={() => handleUpdateStatus(payment.id, 'paid')}
                          className="p-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors"
                          title="Marquer comme payé"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </button>
                      )}
                      
                      {payment.status === 'paid' && (
                        <button
                          onClick={() => handleUpdateStatus(payment.id, 'pending')}
                          className="p-2 bg-yellow-500/20 text-yellow-400 rounded-xl hover:bg-yellow-500/30 transition-colors"
                          title="Marquer comme en attente"
                        >
                          <ClockIcon className="h-5 w-5" />
                        </button>
                      )}
                      
                      <button
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
                        title="Imprimer reçu"
                      >
                        <PrinterIcon className="h-5 w-5" />
                      </button>
                      
                      <Link
                        to={`/payments/${payment.id}/edit`}
                        className="p-2 bg-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/30 transition-colors"
                        title="Modifier"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                      
                      <button
                        onClick={() => handleDeletePayment(payment.id, payment.memberName)}
                        disabled={deletePaymentMutation.isLoading}
                        className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors disabled:opacity-50"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
            <CreditCardIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Aucun paiement trouvé
            </h3>
            <p className="text-white/60 mb-6">
              {searchTerm || statusFilter !== 'all' || methodFilter !== 'all' || dateRange !== 'all'
                ? 'Aucun paiement ne correspond à vos critères de recherche.'
                : 'Aucun paiement enregistré pour le moment.'}
            </p>
            <Link
              to="/payments/new"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 inline-block"
            >
              Créer un Paiement
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PaymentHistory; 